import type { VariantProps } from "class-variance-authority";
import { cn } from "@reactive-resume/utils";
import { HeaderVariants, tableVariants } from "../variants/table";
import { forwardRef } from "react";

export type TableProps = React.HTMLAttributes<HTMLTableElement> &
  VariantProps<typeof tableVariants>;

type HeaderProps = React.HTMLAttributes<HTMLTableSectionElement> &
  VariantProps<typeof HeaderVariants>;

const TableHeader = forwardRef<HTMLTableSectionElement, HeaderProps>(
  ({ className, variant, ...props }, ref) => {
    const Component = "thead";
    return (
      <Component ref={ref} className={cn(HeaderVariants({ className, variant }))} {...props}>
        {props.children}
      </Component>
    );
  },
);

TableHeader.displayName = "TableHeader";

const TableBody = forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => {
  const Component = "tbody";
  return (
    <Component ref={ref} className={cn(className)} {...props}>
      {props.children}
    </Component>
  );
});

TableBody.displayName = "TableBody";

const TableRow = forwardRef<HTMLTableRowElement, React.HTMLAttributes<HTMLTableRowElement>>(
  ({ className, ...props }, ref) => {
    const Component = "tr";
    return (
      <Component ref={ref} className={cn(className)} {...props}>
        {props.children}
      </Component>
    );
  },
);

TableRow.displayName = "TableRow";

type TCellProps = {
  text?: string;
  colspan?: number;
} & React.HTMLAttributes<HTMLTableCellElement>;

const THeaderCell = forwardRef<HTMLTableCellElement, TCellProps>(
  ({ className, text, ...props }, ref) => {
    const Component = "th";
    return (
      <Component ref={ref} className={cn(className)}>
        {text}
      </Component>
    );
  },
);

THeaderCell.displayName = "THeaderCell";

const TBodyCell = forwardRef<HTMLTableCellElement, TCellProps>(
  ({ className, text, colspan = 1, ...props }, ref) => {
    const Component = "td";
    return (
      <Component ref={ref} colSpan={colspan} className={cn(className)}>
        {text}
        {props.children}
      </Component>
    );
  },
);

TBodyCell.displayName = "TBodyCell";

const Table = forwardRef<HTMLTableElement, TableProps>(({ className, ...props }, ref) => (
  <table ref={ref} className={cn(tableVariants({ className }))} {...props}>
    {props.children}
  </table>
));

Table.displayName = "Table";

export { TableHeader, TableBody, TableRow, THeaderCell, TBodyCell, Table };
